import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Sidebar from '../../components/Sidebar/Sidebar';
import VideoPlayer from '../../components/VideoPlayer/VideoPlayer';
import * as firebaseCourses from '../../services/firebaseCourses';
import { getProgress, setLessonComplete } from '../../services/firebaseProgress';
import { createCertificateIfNeeded } from '../../services/firebaseCertificates';
import { awardBadge } from '../../services/firebaseBadges';
import { getMergedEnrollments } from '../../utils/enrollmentStore';
import { isVideoLockedForUser, isTextLesson, hasFullCourseAccess } from '../../utils/lessonUtils';
import { formatCoursePrice } from '../../utils/priceUtils';
import LessonQuiz from '../../components/LessonQuiz/LessonQuiz';
import './CoursePlayer.css';

const TABS = ['Overview', 'Quiz'];

export default function CoursePlayer() {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Overview');
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completedLessonIds, setCompletedLessonIds] = useState([]);
  const [progressPercent, setProgressPercent] = useState(0);
  const [enrolledInCourse, setEnrolledInCourse] = useState(false);
  const lastMarkedLessonRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    firebaseCourses.getCourse(courseId)
      .then((detail) => {
        if (isMounted) setCourse(detail || null);
      })
      .catch(() => {
        if (isMounted) setCourse(null);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => { isMounted = false; };
  }, [courseId]);

  useEffect(() => {
    if (!user?.uid || !courseId) {
      setEnrolledInCourse(false);
      return;
    }
    getMergedEnrollments(user.uid)
      .then((list) => {
        setEnrolledInCourse((list || []).some((c) => String(c.id) === String(courseId)));
      })
      .catch(() => setEnrolledInCourse(false));
  }, [user?.uid, courseId]);

  useEffect(() => {
    if (!user?.uid || !courseId) return;
    getProgress(user.uid, courseId).then(({ completedLessonIds: ids, progressPercent: pct }) => {
      setCompletedLessonIds(ids);
      setProgressPercent(pct || 0);
    });
  }, [user?.uid, courseId]);

  const totalLessons = course?.curriculum?.reduce((acc, s) => acc + (s.lessons?.length || 0), 0) || 0;
  const hasFullAccess = course ? hasFullCourseAccess(course.price, enrolledInCourse) : false;

  const flatLessons = course?.curriculum?.flatMap((s) => s.lessons || []) || [];
  const currentLesson = lessonId
    ? flatLessons.find((l) => String(l.id) === String(lessonId)) ?? null
    : flatLessons[0] || null;

  const videoLocked = currentLesson && isVideoLockedForUser(currentLesson, course?.price, hasFullAccess);

  // Optimistically keep the progress bar in sync with local completed lessons,
  // so it increases immediately on lesson-to-lesson navigation.
  useEffect(() => {
    if (totalLessons < 1) return;
    const optimistic = Math.min(100, Math.round(((completedLessonIds?.length || 0) / totalLessons) * 100));
    setProgressPercent((prev) => (optimistic > prev ? optimistic : prev));
  }, [completedLessonIds, totalLessons]);

  useEffect(() => {
    if (!user?.uid || !courseId || !lessonId || !course || !currentLesson || totalLessons < 1) return;
    if (videoLocked) return;
    if (lastMarkedLessonRef.current === lessonId) return;
    lastMarkedLessonRef.current = lessonId;
    const idStr = String(lessonId);
    setCompletedLessonIds((prev) => (prev.includes(idStr) ? prev : [...prev, idStr]));
    setLessonComplete(user.uid, courseId, lessonId, totalLessons)
      .then((newPercent) => {
        setProgressPercent(newPercent);
        getProgress(user.uid, courseId).then(({ completedLessonIds: ids }) => setCompletedLessonIds(ids));
        if (newPercent >= 100) {
          createCertificateIfNeeded(user.uid, user.name, courseId, course.title).then((certId) => {
            if (certId) awardBadge(user.uid, 'first_course').then(() => {});
          });
          awardBadge(user.uid, 'course_complete').then(() => {});
        }
      })
      .catch(() => {
        setCompletedLessonIds((prev) => prev.filter((id) => id !== idStr));
        getProgress(user.uid, courseId).then(({ completedLessonIds: ids, progressPercent: pct }) => {
          setCompletedLessonIds(ids);
          setProgressPercent(pct || 0);
        });
      });
  }, [user?.uid, user?.name, courseId, lessonId, course?.title, course, currentLesson, totalLessons, videoLocked]);

  const firstLessonId = course?.curriculum?.[0]?.lessons?.[0]?.id;

  useEffect(() => {
    if (!lessonId && firstLessonId) navigate(`/course/${courseId}/learn/${firstLessonId}`, { replace: true });
  }, [lessonId, firstLessonId, courseId, navigate]);

  const isText = isTextLesson(currentLesson);

  // Hard separation: if a text lesson is opened in /learn, redirect to /read.
  // Must be declared before any conditional returns to satisfy react-hooks/rules-of-hooks.
  useEffect(() => {
    if (!courseId || !currentLesson) return;
    if (!isText) return;
    navigate(`/course/${courseId}/read/${currentLesson.id}`, { replace: true });
  }, [courseId, currentLesson, isText, navigate]);

  if (loading || !course) {
    return (
      <div className="course-player-layout">
        <main className="course-player-main">
          <div className="course-player-video-section">
            <p className="course-player-loading">Loading course…</p>
          </div>
        </main>
      </div>
    );
  }

  if (lessonId && !currentLesson) {
    return (
      <div className="course-player-layout">
        <main className="course-player-main">
          <div className="course-player-video-section">
            <p className="course-player-loading">Lesson not found.</p>
            <p className="course-player-lesson-summary">
              <Link to={`/course/${courseId}`}>Return to course</Link>
              {' · '}
              <Link to={`/course/${courseId}/overview`}>Overview</Link>
            </p>
          </div>
        </main>
        <Sidebar
          curriculum={course.curriculum}
          courseId={courseId}
          currentLessonId={lessonId}
          completedLessonIds={completedLessonIds}
          progressPercent={progressPercent}
          coursePrice={course.price}
          hasFullAccess={hasFullAccess}
        />
      </div>
    );
  }

  const lessonVideoSrc = currentLesson?.video_url || currentLesson?.videoUrl || null;
  const lessonPoster = course.banner;
  const coursePriceDisplay = formatCoursePrice(course?.price ?? 'Free');
  const quiz = currentLesson?.quiz || null;

  return (
    <div className="course-player-layout">
      <main className="course-player-main">
        <div className="course-player-video-section">
          {videoLocked ? (
            <div className="course-player-video-locked">
              <div className="course-player-video-locked-inner">
                <span className="course-player-video-locked-icon" aria-hidden="true">🔒</span>
                <h2 className="course-player-video-locked-title">Video locked</h2>
                <p className="course-player-video-locked-text">
                  Purchase this course or complete payment to unlock all video lessons. Text lessons remain available to preview the course.
                </p>
                <p className="course-player-video-locked-price">
                  Course price: <strong>{coursePriceDisplay}</strong>
                </p>
                <Link to={`/course/${courseId}/pay`} className="course-player-video-locked-btn">
                  Pay &amp; enroll
                </Link>
                <Link to={`/course/${courseId}/overview`} className="course-player-video-locked-link">
                  Back to course overview
                </Link>
              </div>
            </div>
          ) : (
            <VideoPlayer
              src={lessonVideoSrc}
              poster={lessonPoster}
              title={currentLesson?.title}
            />
          )}
          <div className="course-player-lesson-header">
            <h1 className="course-player-lesson-title">{currentLesson?.title || 'Select a lesson'}</h1>
            <div className="course-player-links">
              <Link to={`/course/${courseId}/overview`} className="course-player-back">Back to course</Link>
            </div>
          </div>
          <div className="course-player-tabs">
            {TABS.map((tab) => (
              <button
                type="button"
                key={tab}
                className={`course-player-tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="course-player-tab-content">
            {activeTab === 'Overview' && (
              <div className="course-player-overview">
                {course.description && (
                  <p className="course-player-description">{course.description}</p>
                )}
                {((course.whatYouLearn || course.what_you_learn)?.length || course.requirements?.length) && (
                  <div className="course-player-overview-grid">
                    {(course.whatYouLearn || course.what_you_learn)?.length > 0 && (
                      <div className="course-player-overview-column">
                        <h3 className="course-player-overview-heading">What you'll learn</h3>
                        <ul className="course-player-overview-list">
                          {(course.whatYouLearn || course.what_you_learn || []).map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {course.requirements?.length > 0 && (
                      <div className="course-player-overview-column">
                        <h3 className="course-player-overview-heading">Requirements</h3>
                        <ul className="course-player-overview-list">
                          {course.requirements.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                {currentLesson && (
                  <p className="course-player-lesson-summary">
                    Current lesson: <span>{currentLesson.title}</span>
                  </p>
                )}
                {!currentLesson && (
                  <p className="course-player-lesson-summary">
                    Select a lesson from the sidebar to start learning.
                  </p>
                )}
              </div>
            )}
            {activeTab === 'Quiz' && (
              <LessonQuiz
                quiz={quiz}
                userId={user?.uid}
                courseId={courseId}
                lessonId={String(currentLesson?.id || lessonId || '')}
              />
            )}
          </div>
        </div>
      </main>
      <Sidebar
        curriculum={course.curriculum}
        courseId={courseId}
        currentLessonId={lessonId}
        completedLessonIds={completedLessonIds}
        progressPercent={progressPercent}
        coursePrice={course.price}
        hasFullAccess={hasFullAccess}
      />
    </div>
  );
}
